import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {
  Flowchart,
  FlowchartBaseEdge,
  FlowchartBaseNode,
} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  ProcessInteractiveFlowchart,
  ProcessStaticFlowchart,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {getClientIdForProcessFlowchartEdge} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';
import {
  isRootNodeDetailsGQL,
  isOutputNodeDetailsGQL,
} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForGQL';

import {ProcessFlowchartV2GraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessFlowchartV2GraphQL';
import * as InteractiveNodeTools from './makeProcessFlowchartInteractiveNode';
import * as StaticNodeTools from './makeProcessFlowchartStaticNode';

type ProcessFlowchartNodeGraphQL = ProcessFlowchartV2GraphQL['nodeConfigs'][number];

function makeBaseEdge({
  clientId,
  fromNodeClientId,
  toNodeClientId,
}: {
  clientId: string;
  fromNodeClientId: string;
  toNodeClientId: string;
}): FlowchartBaseEdge {
  return {
    clientId,
    fromNodeClientId,
    toNodeClientId,
  };
}

export function convertFlowchartV2GqlToProcessStaticFlowchart(
  flowchartGql: ProcessFlowchartV2GraphQL
): ProcessStaticFlowchart {
  return convertFlowchartV2GqlToProcessFlowchart({
    flowchartGql,
    makeRootNode: (name, taskSchema, clientId) => StaticNodeTools.makeRootNode(name, clientId),
    makeTaskOrControlNode: StaticNodeTools.makeTaskOrControlNode,
    makeOutputNode: (_, clientId) => StaticNodeTools.makeOutputNode(clientId),
    makeEdge: makeBaseEdge,
  });
}

export function convertFlowchartV2GqlToProcessInteractiveFlowchart(
  flowchartGql: ProcessFlowchartV2GraphQL
): ProcessInteractiveFlowchart {
  return convertFlowchartV2GqlToProcessFlowchart({
    flowchartGql,
    makeRootNode: InteractiveNodeTools.makeRootNode,
    makeTaskOrControlNode: InteractiveNodeTools.makeTaskOrControlNode,
    makeOutputNode: InteractiveNodeTools.makeOutputNode,
    makeEdge: makeBaseEdge,
  });
}

interface TaskSchemaForFlowchart {
  id: string;
  name: string;
}

function convertFlowchartV2GqlToProcessFlowchart<
  TNode extends FlowchartBaseNode,
  TEdge extends FlowchartBaseEdge,
>({
  flowchartGql,
  makeRootNode,
  makeTaskOrControlNode,
  makeOutputNode,
  makeEdge,
}: {
  flowchartGql: ProcessFlowchartV2GraphQL;
  makeRootNode: (name: string, taskSchema: TaskSchemaForFlowchart, clientId: string) => TNode;
  makeTaskOrControlNode: (
    nodeGQL: ProcessFlowchartNodeGraphQL,
    gqlEdgeIdsToClientEdges?: {[id: string]: [TEdge]}
  ) => TNode;
  makeOutputNode: (taskSchema: TaskSchemaForFlowchart, clientId: string) => TNode;
  makeEdge: (params: {clientId: string; fromNodeClientId: string; toNodeClientId: string}) => TEdge;
}): Flowchart<TNode, TEdge> {
  // TODO(haroon): this can just be Record<string, TEdge>
  const gqlEdgeIdsToClientEdges: Record<string, [TEdge]> = {};
  const edges: Array<TEdge> = [];
  for (const edgeGql of flowchartGql.edgeConfigs) {
    const edge = makeEdge({
      clientId: getClientIdForProcessFlowchartEdge(edgeGql.fromNodeConfig.id, edgeGql.toNodeConfig.id),
      fromNodeClientId: edgeGql.fromNodeConfig.id,
      toNodeClientId: edgeGql.toNodeConfig.id,
    });
    edges.push(edge);
    gqlEdgeIdsToClientEdges[edgeGql.id] = [edge];
  }
  const nodes = flowchartGql.nodeConfigs.map((nodeConfig) => {
    if (isRootNodeDetailsGQL(nodeConfig.nodeDetails)) {
      return makeRootNode(
        nodeConfig.name,
        assertIsAccessible(nodeConfig.nodeDetails.taskSchema),
        nodeConfig.id
      );
    } else if (isOutputNodeDetailsGQL(nodeConfig.nodeDetails)) {
      return makeOutputNode(
        assertIsAccessible(nodeConfig.nodeDetails.outputSchema).taskSchema,
        nodeConfig.id
      );
    } else {
      return makeTaskOrControlNode(nodeConfig, gqlEdgeIdsToClientEdges);
    }
  });
  return {
    clientId: flowchartGql.id,
    nodes,
    edges,
  };
}
